<template>
  <v-container fluid class="performance-v2-container pa-0 ma-0">
    <div class="performance-v2-shell">
      <v-progress-linear
        v-if="preloader"
        class="loading-bar"
        color="#1D9E75"
        height="3"
        indeterminate
      />

      <section v-if="showHeader" class="header-section performance-v2-hero">
        <v-btn icon small class="header-close-btn" @click="dismissHeader">
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>
        <v-container fluid class="px-3">
          <v-row align="center" class="pa-0 ma-0">
            <v-col cols="12" md="8" class="d-flex align-center pa-0">
              <div class="header-icon-wrapper">
                <v-icon size="32" color="white">mdi-chart-line-variant</v-icon>
              </div>
              <div class="ml-4">
                <h1 class="page-title">Productivity &amp; Performance</h1>
                <p class="page-subtitle">
                  Analyze individual call activity, dispositions, dialer activity presence, and time quality by month.
                </p>
              </div>
            </v-col>
            <v-col cols="12" md="4" class="text-right pa-0 performance-hero-chips">
              <v-chip class="ma-1 feature-chip" outlined>
                <v-icon left small>mdi-chart-areaspline</v-icon>
                Analytics
              </v-chip>
              <v-chip v-if="!$store.state.user.permissions.disableExportList" class="ma-1 feature-chip" outlined>
                <v-icon left small>mdi-file-export</v-icon>
                Export
              </v-chip>
            </v-col>
          </v-row>
          <v-row class="mt-4">
            <v-col cols="12" class="pa-0">
              <div class="feature-pills-container">
                <div class="feature-pill">
                  <v-icon small class="mr-2" color="white">mdi-chart-donut</v-icon>
                  Performance Charts
                </div>
                <div class="feature-pill">
                  <v-icon small class="mr-2" color="white">mdi-phone-log</v-icon>
                  Call Statistics
                </div>
                <div class="feature-pill">
                  <v-icon small class="mr-2" color="white">mdi-calendar-month</v-icon>
                  Monthly Reports
                </div>
                <div class="feature-pill">
                  <v-icon small class="mr-2" color="white">mdi-account-search</v-icon>
                  User Selection
                </div>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </section>

      <section class="dashboard-card agent-header-card">
        <div class="agent-header-layout">
          <div class="agent-profile">
            <div class="agent-avatar">{{ accountInitials }}</div>
            <div class="agent-copy">
              <h1>{{ agentName }}</h1>
              <p>
                {{ agentRole }}
                <span class="copy-dot"></span>
                Last seen
                <span :class="lastSeenClass">{{ formattedLastSeen }}</span>
              </p>
              <div class="badge-row">
                <span class="status-badge status-badge-neutral">
                  {{ selectedMonthLabel }}
                </span>
              </div>
            </div>
          </div>

          <button
            type="button"
            class="classic-view-toggle classic-view-toggle-active performance-classic-view-toggle"
            role="switch"
            aria-checked="true"
            @click="goToClassicPerformance"
          >
            <span class="classic-view-toggle-label classic-view-classic-label">Classic View</span>
            <span class="classic-view-switch" aria-hidden="true">
              <span class="classic-view-switch-thumb"></span>
            </span>
            <span class="classic-view-toggle-label classic-view-modern-label">Modern View</span>
          </button>

          <div class="header-controls">
            <v-select
              v-model="date"
              :items="monthOptions"
              class="control-select"
              dense
              hide-details
              item-text="text"
              item-value="value"
              label="Viewing month"
              outlined
              @change="onMonthChange"
            />
            <v-select
              v-model="accountId"
              :items="accounts"
              class="control-select agent-select"
              dense
              hide-details
              item-text="name"
              item-value="id"
              label="Agent"
              outlined
              @change="onAgentChange"
            />
            <v-btn
              v-if="!$store.state.user.permissions.disableExportList"
              class="export-report-btn"
              color="#37cf37"
              depressed
              :disabled="!exportPerformenceResponse.length"
              :loading="exportLoader"
              @click="exportPerformanceReport"
            >
              <v-icon left small>mdi-file-export</v-icon>
              Export Report
            </v-btn>
          </div>
        </div>
      </section>

      <section class="kpi-grid" aria-label="Performance KPIs">
        <article v-for="card in kpiCards" :key="card.label" class="kpi-card">
          <p class="kpi-label">{{ card.label }}</p>
          <p class="kpi-value">{{ card.value }}</p>
          <p :class="['kpi-sub', card.tone]">{{ card.subLabel }}</p>
        </article>
      </section>

      <section class="panel-grid panel-grid-top">
        <article class="dashboard-card daily-card">
          <div class="section-heading daily-heading">
            <div>
              <h2>Daily Call Activity - {{ selectedMonthLabel }}</h2>
              <p>Calls and active time per day</p>
            </div>
            <div class="legend-row">
              <span><i class="legend-dot calls-dot"></i>Calls</span>
              <span><i class="legend-dot active-dot"></i>Active time</span>
            </div>
          </div>

          <div
            class="daily-chart"
            :style="{
              gridTemplateColumns:
                'repeat(' + dailyActivity.length + ', minmax(10px, 1fr))',
            }"
          >
            <div class="chart-guide chart-guide-top"></div>
            <div class="chart-guide chart-guide-mid"></div>
            <div class="chart-guide chart-guide-low"></div>

            <div v-for="day in dailyActivity" :key="day.dateKey" class="day-column">
              <div class="bar-pair">
                <div
                  class="bar-slot"
                  tabindex="0"
                  :aria-label="day.dateKey + ' number of calls ' + day.calls"
                >
                  <div
                    :class="[
                      'activity-bar',
                      day.isStub ? 'stub-bar' : 'calls-bar',
                    ]"
                    :style="{ height: day.callHeight + '%' }"
                  ></div>
                  <div class="activity-tooltip">
                    <strong>{{ day.dateKey }}</strong>
                    <span>
                      <i class="tooltip-dot tooltip-calls-dot"></i>
                      Number of calls: {{ day.calls }}
                    </span>
                  </div>
                </div>
                <div
                  class="bar-slot"
                  tabindex="0"
                  :aria-label="
                    day.dateKey +
                      ' active time ' +
                      formatHoursCompact(day.activeMinutes)
                  "
                >
                  <div
                    :class="[
                      'activity-bar',
                      day.isStub ? 'stub-bar' : 'active-bar',
                    ]"
                    :style="{ height: day.activeHeight + '%' }"
                  ></div>
                  <div class="activity-tooltip">
                    <strong>{{ day.dateKey }}</strong>
                    <span>
                      <i class="tooltip-dot tooltip-active-dot"></i>
                      Active time: {{ formatHoursCompact(day.activeMinutes) }}
                    </span>
                  </div>
                </div>
              </div>
              <span>{{ day.day }}</span>
            </div>
          </div>
        </article>

        <article
          class="dashboard-card disposition-card clickable-card"
          :aria-label="'View all dispositions for ' + selectedMonthLabel"
          :role="dispositionSummary.length ? 'button' : null"
          :tabindex="dispositionSummary.length ? 0 : -1"
          @click="openDispositionDialog"
          @keydown.enter="openDispositionDialog"
          @keydown.space.prevent="openDispositionDialog"
        >
          <div class="section-heading">
            <div>
              <h2>Disposition Breakdown - {{ totalCalls }} total calls</h2>
              <p>Top outcomes, sorted by volume</p>
            </div>
          </div>

          <div v-if="dispositionBars.length" class="disposition-list">
            <div
              v-for="item in dispositionBars"
              :key="item.label"
              class="disposition-row"
            >
              <span class="disposition-label">{{ item.label }}</span>
              <div class="disposition-track">
                <div
                  class="disposition-fill"
                  :style="{ width: item.width + '%', backgroundColor: item.color }"
                ></div>
              </div>
              <span class="disposition-count">{{ item.count }}</span>
            </div>
          </div>
          <div v-else class="empty-state">No dispositions in this month</div>
        </article>
      </section>

      <section class="panel-grid panel-grid-bottom">
        <article class="dashboard-card attendance-card">
          <div class="section-heading">
            <div>
              <h2>Dialer Activity Presence - {{ selectedMonthLabel }}</h2>
              <p>{{ daysPresent }} of {{ daysInSelectedMonth }} days present</p>
            </div>
          </div>

          <div class="weekday-grid">
            <span v-for="day in weekdayLabels" :key="day">{{ day }}</span>
          </div>
          <div class="calendar-grid">
            <div
              v-for="cell in calendarCells"
              :key="cell.key"
              :class="[
                'calendar-cell',
                cell.blank ? 'blank-cell' : cell.typeClass,
                { 'today-cell': cell.isToday },
              ]"
            >
              <span v-if="!cell.blank">{{ cell.day }}</span>
            </div>
          </div>

          <div class="legend-row legend-row-bottom calendar-legend">
            <span><i class="calendar-key present-key"></i>Present</span>
            <span><i class="calendar-key absent-key"></i>Absent</span>
            <span><i class="calendar-key today-key"></i>Today</span>
          </div>
        </article>

        <article class="dashboard-card quality-card">
          <div class="section-heading">
            <div>
              <h2>Time Quality</h2>
              <p>How {{ agentFirstName }}'s on-duty hours are spent</p>
            </div>
          </div>

          <div class="quality-bar">
            <div
              v-for="segment in timeQualitySegments"
              :key="segment.key"
              :class="['quality-segment', segment.className]"
              :style="{ width: segment.width + '%' }"
            >
              <span v-if="segment.showLabel">{{ segment.label }}</span>
            </div>
          </div>

          <div class="quality-list">
            <div v-for="row in timeQualityRows" :key="row.key" class="quality-row">
              <div class="quality-label-wrap">
                <span :class="['quality-dot', row.className]"></span>
                <span>{{ row.label }}</span>
              </div>
              <div class="quality-value-wrap">
                <strong>{{ row.hours }}</strong>
                <span>{{ row.percent }}</span>
              </div>
            </div>
          </div>

          <div v-if="showIdleWarning" class="idle-warning">
            <p>High idle time detected</p>
            <span>
              {{ idlePercentText }} vs team average {{ teamAverageIdleText }}
            </span>
          </div>
        </article>
      </section>

      <v-dialog v-model="dispositionDialog" max-width="560">
        <v-card class="disposition-dialog-card">
          <div class="disposition-dialog-header">
            <div>
              <h2>All dispositions</h2>
              <p>{{ totalCalls }} total calls - {{ selectedMonthLabel }}</p>
            </div>
            <v-btn icon small @click="dispositionDialog = false">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </div>

          <div class="disposition-dialog-list">
            <div
              v-for="item in dispositionSummary"
              :key="'dialog-' + item.label"
              class="disposition-dialog-row"
            >
              <div class="disposition-dialog-name">
                <span
                  class="disposition-dialog-dot"
                  :style="{ backgroundColor: item.color }"
                ></span>
                <span>{{ item.label }}</span>
              </div>
              <strong>{{ formatNumber(item.count) }}</strong>
            </div>
          </div>
        </v-card>
      </v-dialog>
    </div>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment-timezone";
import * as XLSX from "xlsx/xlsx.mjs";

export default {
  data() {
    return {
      accountId: "",
      accountDetails: null,
      accounts: [],
      dailyStats: [],
      date: new Date().toISOString().substr(0, 7),
      dateRange: [],
      exportLoader: false,
      dispositionDialog: false,
      dispositionSummary: [],
      exportPerformenceResponse: [],
      lastSeen: "",
      monthOptions: [],
      officeTime: 0,
      preloader: false,
      rawStats: [],
      showHeader: true,
      teamAverageIdlePercent: 61,
      weekdayLabels: ["M", "T", "W", "T", "F", "S", "S"],
    };
  },
  computed: {
    ...mapGetters(["dispoList", "selectedTeam", "teams"]),
    accountInitials() {
      return this.getInitials(this.agentName);
    },
    agentFirstName() {
      return this.agentName.split(" ")[0] || "Agent";
    },
    agentName() {
      return this.accountDetails && this.accountDetails.name
        ? this.accountDetails.name
        : "Select agent";
    },
    agentRole() {
      return this.accountDetails && this.accountDetails.role
        ? this.accountDetails.role
        : "Team member";
    },
    activePercent() {
      if (!this.onDutyMinutes) return 0;
      return (this.totalActiveMinutes / this.onDutyMinutes) * 100;
    },
    averageCallDurationSeconds() {
      if (!this.totalCalls) return 0;
      return this.totalDurationSeconds / this.totalCalls;
    },
    averageCallsPerPresentDay() {
      if (!this.daysPresent) return 0;
      return Math.round(this.totalCalls / this.daysPresent);
    },
    calendarCells() {
      const start = moment(this.date, "YYYY-MM").startOf("month");
      const daysInMonth = start.daysInMonth();
      const leadingBlanks = start.isoWeekday() - 1;
      const cells = [];

      for (let i = 0; i < leadingBlanks; i++) {
        cells.push({ blank: true, key: "blank-start-" + i });
      }

      for (let day = 1; day <= daysInMonth; day++) {
        const current = start.clone().date(day);
        const dateKey = current.format("YYYY-MM-DD");
        const isPresent = this.presentDayKeys.indexOf(dateKey) > -1;
        const isFuture = current.isAfter(moment(), "day");
        const typeClass = isPresent
          ? "present-day"
          : isFuture
          ? "future-day"
          : "absent-day";

        cells.push({
          blank: false,
          dateKey,
          day,
          isToday: current.isSame(moment(), "day"),
          key: dateKey,
          typeClass,
        });
      }

      const trailingBlanks = (7 - (cells.length % 7)) % 7;
      for (let i = 0; i < trailingBlanks; i++) {
        cells.push({ blank: true, key: "blank-end-" + i });
      }

      return cells;
    },
    dailyActivity() {
      const maxCalls = Math.max.apply(
        null,
        this.dailyStats.map((day) => day.calls).concat([1]),
      );
      const maxActive = Math.max.apply(
        null,
        this.dailyStats.map((day) => day.activeMinutes).concat([1]),
      );

      return this.dailyStats.map((day) => {
        const isStub = day.calls <= 0 && day.activeMinutes <= 0;
        return Object.assign({}, day, {
          activeHeight: isStub
            ? 4
            : Math.max(Math.round((day.activeMinutes / maxActive) * 100), 8),
          callHeight: isStub
            ? 4
            : Math.max(Math.round((day.calls / maxCalls) * 100), 8),
          isStub,
        });
      });
    },
    daysPresent() {
      return this.presentDayKeys.length;
    },
    dispositionBars() {
      if (!this.dispositionSummary.length) return [];

      const topRows = this.dispositionSummary.slice(0, 8);
      const remainingRows = this.dispositionSummary.slice(8);
      const rows = topRows.slice();

      if (remainingRows.length) {
        rows.push({
          category: "other",
          color: "#B4B2A9",
          count: remainingRows.reduce((sum, item) => sum + item.count, 0),
          label: "Others",
        });
      }

      const maxCount = Math.max.apply(
        null,
        rows.map((item) => item.count).concat([1]),
      );

      return rows.map((item) =>
        Object.assign({}, item, {
          width: Math.max(Math.round((item.count / maxCount) * 100), 6),
        }),
      );
    },
    formattedLastSeen() {
      if (!this.lastSeen) return "no activity yet";

      const lastSeenMoment = moment(this.lastSeen);
      if (lastSeenMoment.isSame(moment(), "day")) {
        return "today at " + lastSeenMoment.format("h:mm A");
      }

      return lastSeenMoment.format("MMM D, YYYY h:mm A");
    },
    idleMinutes() {
      return Math.max(this.onDutyMinutes - this.totalActiveMinutes, 0);
    },
    idlePercent() {
      if (!this.onDutyMinutes) return 0;
      return (this.idleMinutes / this.onDutyMinutes) * 100;
    },
    idlePercentText() {
      return this.formatPercent(this.idlePercent);
    },
    idleRawMinutesLabel() {
      return this.formatNumber(Math.round(this.idleMinutes)) + " mins total";
    },
    kpiCards() {
      return [
        {
          label: "Total calls",
          subLabel: "This month",
          value: this.formatNumber(this.totalCalls),
        },
        {
          label: "Daily average",
          subLabel: "Calls per active day",
          value: this.formatNumber(this.averageCallsPerPresentDay),
        },
        {
          label: "Avg call duration",
          subLabel: "",
          value: this.formatDuration(this.averageCallDurationSeconds),
        },
        {
          label: "Days present",
          subLabel: "of " + this.daysInSelectedMonth + " days",
          value: this.formatNumber(this.daysPresent),
        },
        {
          label: "Idle time",
          subLabel: this.idleRawMinutesLabel,
          value: Math.round(this.idleMinutes / 60) + "h",
        },
      ];
    },
    lastSeenClass() {
      return this.lastSeen && moment(this.lastSeen).isSame(moment(), "day")
        ? "last-seen-today"
        : "last-seen-muted";
    },
    liveStatusClass() {
      if (this.liveStatusText == "On call") return "status-badge-success";
      if (this.liveStatusText == "Available") return "status-badge-info";
      return "status-badge-offline";
    },
    liveStatusText() {
      const status = this.accountDetails && this.accountDetails.status;
      if (typeof status == "string") {
        const statusText = status.toLowerCase();
        if (statusText == "available") return "Available";
        if (statusText == "offline") return "Offline";
        if (statusText == "oncall" || statusText == "on call") return "On call";
      }

      if (this.lastSeen && moment(this.lastSeen).isSame(moment(), "day")) {
        return "On call";
      }

      return "Offline";
    },
    onDutyMinutes() {
      return this.officeTime * this.daysPresent;
    },
    presentDayKeys() {
      return this.dailyStats
        .filter((day) => day.calls > 0)
        .map((day) => day.dateKey);
    },
    selectedMonthLabel() {
      return moment(this.date, "YYYY-MM").format("MMMM YYYY");
    },
    showIdleWarning() {
      return this.idlePercent > this.teamAverageIdlePercent;
    },
    teamAverageIdleText() {
      return this.formatPercent(this.teamAverageIdlePercent);
    },
    timeQualityRows() {
      return [
        {
          className: "quality-active",
          hours: this.formatHours(this.totalActiveMinutes),
          key: "active",
          label: "Active call time",
          percent: this.formatPercent(this.activePercent),
        },
        {
          className: "quality-idle",
          hours: this.formatHours(this.idleMinutes),
          key: "idle",
          label: "Idle time",
          percent: this.formatPercent(this.idlePercent),
        },
      ];
    },
    timeQualitySegments() {
      const segments = [
        {
          className: "segment-active",
          key: "active",
          percent: this.activePercent,
        },
        {
          className: "segment-idle",
          key: "idle",
          percent: this.idlePercent,
        },
      ];

      if (!this.onDutyMinutes) {
        return segments.map((segment) =>
          Object.assign({}, segment, {
            label: "0%",
            showLabel: false,
            width: segment.key == "idle" ? 100 : 0,
          }),
        );
      }

      return segments.map((segment) => {
        const width = Math.max(segment.percent, 0);
        return Object.assign({}, segment, {
          label:
            segment.key == "idle"
              ? this.formatPercent(segment.percent) + " idle"
              : this.formatPercent(segment.percent),
          showLabel: width >= 8,
          width,
        });
      });
    },
    totalActiveMinutes() {
      return this.dailyStats.reduce((sum, day) => sum + day.activeMinutes, 0);
    },
    totalCalls() {
      return this.dailyStats.reduce((sum, day) => sum + day.calls, 0);
    },
    totalDurationSeconds() {
      return this.dailyStats.reduce((sum, day) => sum + day.durationSeconds, 0);
    },
    daysInSelectedMonth() {
      return moment(this.date, "YYYY-MM").daysInMonth();
    },
    wrapUpMinutes() {
      return this.rawStats.reduce((sum, row) => {
        const values = [
          row.wrapUpMinutes,
          row.wrapupMinutes,
          row.adminMinutes,
          row.wrapUpTime,
        ];
        const minutes = values.reduce((matched, value) => {
          if (matched > 0) return matched;
          return Number(value) || 0;
        }, 0);
        return sum + minutes;
      }, 0);
    },
    wrapUpPercent() {
      if (!this.onDutyMinutes) return 0;
      return (this.wrapUpMinutes / this.onDutyMinutes) * 100;
    },
  },
  watch: {
    selectedTeam() {
      this.onCreate();
    },
  },
  created() {
    this.showHeader = localStorage.getItem("performanceV2HeaderDismissed") !== "true";
    this.prepareMonthOptions();
    this.onCreate();
  },
  methods: {
    dismissHeader() {
      this.showHeader = false;
      localStorage.setItem("performanceV2HeaderDismissed", "true");
    },
    goToClassicPerformance() {
      this.$router.push({ path: "/performance" });
    },
    exportPerformanceReport() {
      if (!this.exportPerformenceResponse.length) {
        if (this.$swal) {
          this.$swal({
            type: "warning",
            text: "No records to export",
          });
        }
        return;
      }

      this.exportLoader = true;

      try {
        const exportData = this.exportPerformenceResponse.map((row) => {
          return {
            Name: this.agentName,
            Email: (this.accountDetails && this.accountDetails.email) || "",
            Date: moment(row._id || row.modifiedOn).format("YYYY-MM-DD"),
            Calls: Number(row.calls) || 0,
            ActiveTimeMinutes: Number(
              (Number(row.timeSpentOnCall || 0) / 1000 / 60).toFixed(1),
            ),
            DurationSeconds: Number(
              (Number(row.duration || 0) / 1000).toFixed(1),
            ),
            Status: this.formatStatusForExport(row.status),
          };
        });

        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Performance V2");
        XLSX.writeFile(
          workbook,
          `performance_v2_${this.agentName}_${this.date}_${new Date().getTime()}.xlsx`,
        );
      } catch (error) {
        console.log("Error exporting performance report", error);
        if (this.$swal) {
          this.$swal({
            type: "error",
            text: "Something went wrong while exporting reports.",
          });
        }
      } finally {
        this.exportLoader = false;
      }
    },
    async getAccountDetails() {
      if (!this.accountId) return;

      let selectedAccount = this.accounts.find((account) => {
        return account.id == this.accountId;
      });

      if (!selectedAccount) {
        const filter = {
          include: [
            {
              relation: "lists",
              scope: { fields: ["id", "name"] },
            },
            {
              relation: "teams",
              scope: { fields: ["id", "name"] },
            },
          ],
        };

        const response = await this.$http.get(
          `${this.$store.state.ENDPOINT}/accounts/${
            this.accountId
          }?filter=${encodeURIComponent(JSON.stringify(filter))}`,
        );
        selectedAccount = response.body;
      }

      this.accountDetails = selectedAccount;
    },
    async getAccountId() {
      const teamIds = this.getTeamIds();
      const response = await this.$http.post(
        `${this.$store.state.ENDPOINT}/accounts/fetch-v2`,
        { teams: teamIds },
      );

      this.accounts = response.body || [];

      if (this.$route.query.userid) {
        this.accountId = this.$route.query.userid;
      } else if (!this.accountId && this.accounts.length) {
        this.accountId = this.accounts[0].id;
      } else if (
        this.accountId &&
        !this.accounts.find((account) => account.id == this.accountId)
      ) {
        this.accountId = this.accounts.length ? this.accounts[0].id : "";
      }
    },
    async getLogsDetail() {
      if (!this.accountId) return;

      this.preloader = true;
      this.setDates();

      try {
        const dates = [
          moment(this.dateRange[0]).format("YYYY-MM-DDT00:00:00"),
          moment(this.dateRange[1]).format("YYYY-MM-DDT23:59:59"),
        ];
        const response = await this.$http.post(
          `${this.$store.state.ENDPOINT}/accounts/${this.accountId}/stats`,
          { dates },
        );
        const stats = response.body || [];

        this.rawStats = stats;
        this.exportPerformenceResponse = stats;
        this.lastSeen = stats.length && stats[0].modifiedOn ? stats[0].modifiedOn : "";
        this.dailyStats = this.buildDailyStats(stats);
        this.dispositionSummary = this.buildDispositionSummary(stats);
      } catch (error) {
        console.log("Error fetching performance stats", error);
        this.rawStats = [];
        this.exportPerformenceResponse = [];
        this.dailyStats = this.buildDailyStats([]);
        this.dispositionSummary = [];
        this.lastSeen = "";
      } finally {
        this.preloader = false;
      }
    },
    async onAgentChange() {
      await this.getAccountDetails();
      await this.getLogsDetail();
    },
    async onCreate() {
      try {
        this.setDates();
        this.setOfficeTime();
        await this.getAccountId();
        await this.getAccountDetails();
        await this.getLogsDetail();
      } catch (error) {
        console.log("Error loading performance report", error);
        this.preloader = false;
      }
    },
    onMonthChange() {
      this.getLogsDetail();
    },
    openDispositionDialog() {
      if (!this.dispositionSummary.length) return;
      this.dispositionDialog = true;
    },
    buildDailyStats(stats) {
      const start = moment(this.date, "YYYY-MM").startOf("month");
      const daysInMonth = start.daysInMonth();
      const statsByDate = {};

      stats.forEach((row) => {
        const dateSource = row._id || row.modifiedOn;
        const dateKey = moment(dateSource).format("YYYY-MM-DD");
        const calls = Number(row.calls) || 0;
        const activeMinutes = Number(row.timeSpentOnCall || 0) / 1000 / 60;
        const durationSeconds = Number(row.duration || 0) / 1000;

        if (!statsByDate[dateKey]) {
          statsByDate[dateKey] = {
            activeMinutes: 0,
            calls: 0,
            durationSeconds: 0,
          };
        }

        statsByDate[dateKey].activeMinutes += activeMinutes;
        statsByDate[dateKey].calls += calls;
        statsByDate[dateKey].durationSeconds += durationSeconds;
      });

      const days = [];
      for (let day = 1; day <= daysInMonth; day++) {
        const current = start.clone().date(day);
        const dateKey = current.format("YYYY-MM-DD");
        const stat = statsByDate[dateKey] || {
          activeMinutes: 0,
          calls: 0,
          durationSeconds: 0,
        };

        days.push({
          activeMinutes: stat.activeMinutes,
          calls: stat.calls,
          dateKey,
          day,
          durationSeconds: stat.durationSeconds,
        });
      }

      return days;
    },
    buildDispositionSummary(stats) {
      const dispositionMap = {};

      stats.forEach((row) => {
        const statuses = Array.isArray(row.status)
          ? row.status
          : Object.keys(row.status || {}).map((key) => row.status[key]);

        statuses.forEach((status) => {
          if (!status) return;
          const rawLabel = status._id || status.title || status.name || "Others";
          const label = this.toReadableDisposition(rawLabel);
          const count = Number(status.count) || 0;

          if (!dispositionMap[label]) {
            dispositionMap[label] = {
              category: this.getDispositionCategory(label),
              color: this.getDispositionColor(rawLabel, label, status),
              count: 0,
              label,
            };
          }

          dispositionMap[label].count += count;
        });
      });

      return Object.keys(dispositionMap)
        .map((key) => dispositionMap[key])
        .filter((item) => item.count > 0)
        .sort((a, b) => b.count - a.count);
    },
    formatDuration(seconds) {
      const totalSeconds = Math.round(Number(seconds) || 0);
      const minutes = Math.floor(totalSeconds / 60);
      const remainingSeconds = totalSeconds % 60;

      if (!minutes) return remainingSeconds + "s";
      if (!remainingSeconds) return minutes + "m";
      return minutes + "m " + remainingSeconds + "s";
    },
    formatHours(minutes) {
      const roundedHours = Math.round(((Number(minutes) || 0) / 60) * 2) / 2;
      if (roundedHours % 1 == 0) return roundedHours.toFixed(0) + "h";
      return roundedHours.toFixed(1) + "h";
    },
    formatHoursCompact(minutes) {
      const roundedHours = Math.round(((Number(minutes) || 0) / 60) * 10) / 10;
      if (roundedHours < 1) return Math.round(Number(minutes) || 0) + "m";
      return roundedHours + "h";
    },
    formatNumber(value) {
      return new Intl.NumberFormat("en-IN", {
        maximumFractionDigits: 0,
      }).format(Number(value) || 0);
    },
    formatPercent(value) {
      return (Number(value) || 0).toFixed(1) + "%";
    },
    formatStatusForExport(status) {
      const statuses = Array.isArray(status)
        ? status
        : Object.keys(status || {}).map((key) => status[key]);

      return statuses
        .filter((item) => item)
        .map((item) => {
          const label = item._id || item.title || item.name || "Others";
          return `${label}-${Number(item.count) || 0}`;
        })
        .join(",");
    },
    getDispositionCategory(label) {
      const key = this.normalizeDisposition(label);
      const positive = ["interested", "demoscheduled", "underprocess", "support"];
      const followUp = ["willcalllater", "havequeries", "callback"];
      const negative = ["foreign", "notpickedup", "notinterested", "busy"];

      if (positive.indexOf(key) > -1) return "positive";
      if (followUp.indexOf(key) > -1) return "follow-up";
      if (negative.indexOf(key) > -1) return "negative";
      return "other";
    },
    getCategoryDispositionColor(label) {
      const category = this.getDispositionCategory(label);
      if (category == "positive") return "#1D9E75";
      if (category == "follow-up") return "#EF9F27";
      if (category == "negative") return "#7F77DD";
      return "#B4B2A9";
    },
    getDispositionColor(rawLabel, label, status) {
      if (status && (status.hex || status.color || status.colour)) {
        return status.hex || status.color || status.colour;
      }

      const configuredDispo = this.findDispositionByLabel(rawLabel, label);
      if (configuredDispo && configuredDispo.hex) return configuredDispo.hex;

      return this.getCategoryDispositionColor(label);
    },
    findDispositionByLabel(rawLabel, label) {
      const rawKey = this.normalizeDisposition(rawLabel);
      const labelKey = this.normalizeDisposition(label);

      return (this.dispoList || []).find((disposition) => {
        return [
          disposition.title,
          disposition.name,
          disposition.label,
          disposition._id,
        ].some((candidate) => {
          const candidateKey = this.normalizeDisposition(candidate);
          return candidateKey == rawKey || candidateKey == labelKey;
        });
      });
    },
    getInitials(name) {
      return (
        String(name || "")
          .split(" ")
          .filter((part) => part)
          .slice(0, 2)
          .map((part) => part.charAt(0).toUpperCase())
          .join("") || "--"
      );
    },
    getTeamIds() {
      if (this.selectedTeam == "allTeams") {
        return this.teams
          .filter((team) => team.id && team.id.toString() != "allTeams")
          .map((team) => team.id);
      }

      return [this.selectedTeam];
    },
    normalizeDisposition(label) {
      return String(label || "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
    },
    prepareMonthOptions() {
      const currentMonth = moment().startOf("month");
      this.monthOptions = [];

      for (let i = 0; i < 12; i++) {
        const optionMonth = currentMonth.clone().subtract(i, "months");
        this.monthOptions.push({
          text: optionMonth.format("MMMM YYYY"),
          value: optionMonth.format("YYYY-MM"),
        });
      }
    },
    setDates() {
      const startDate = moment(this.date, "YYYY-MM")
        .startOf("month")
        .format("YYYY-MM-DD");
      const endDate = moment(this.date, "YYYY-MM")
        .endOf("month")
        .format("YYYY-MM-DD");
      this.dateRange = [startDate, endDate];
    },
    setOfficeTime() {
      const companyDetails = this.$store.state.companyDetails || {};
      if (!companyDetails.officeStartTime || !companyDetails.officeEndTime) {
        this.officeTime = 0;
        return;
      }

      const officeStart = this.$moment(companyDetails.officeStartTime, "HH:mm");
      const officeEnd = this.$moment(companyDetails.officeEndTime, "HH:mm");
      this.officeTime = Math.max(
        this.$moment.duration(officeEnd.diff(officeStart)).asMinutes(),
        0,
      );
      this.teamAverageIdlePercent = Number(companyDetails.teamAverageIdlePercent) || 61;
    },
    toReadableDisposition(rawLabel) {
      return String(rawLabel || "Others")
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase()
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
    },
  },
};
</script>

<style scoped>
.performance-v2-container {
  background: #f7f8f5;
  min-height: 100vh;
}

.performance-v2-shell {
  padding: 16px;
}

.performance-v2-hero {
  margin: 0 0 16px;
  position: relative;
}

.performance-hero-chips {
  padding-right: 18px !important;
}

.performance-v2-hero .feature-chip {
  background: rgba(255, 255, 255, 0.2) !important;
  border-color: rgba(255, 255, 255, 0.42) !important;
  color: #ffffff !important;
  font-weight: 500;
}

.performance-v2-hero .feature-chip >>> .v-chip__content,
.performance-v2-hero .feature-chip >>> .v-icon {
  color: #ffffff !important;
}

.loading-bar {
  border-radius: 999px;
  margin-bottom: 8px;
}

.dashboard-card {
  background: #ffffff;
  border: 1px solid #deded9;
  border-radius: 10px;
  box-shadow: none;
}

.clickable-card {
  cursor: pointer;
}

.clickable-card:focus-visible {
  box-shadow: 0 0 0 3px rgba(55, 207, 55, 0.22);
  outline: none;
}

.agent-header-card {
  margin-bottom: 14px;
  overflow: hidden;
  padding: 18px 20px;
  position: relative;
}

.agent-header-card::before {
  background: linear-gradient(90deg, #37cf37 0%, #2ba82b 100%);
  content: "";
  height: 4px;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
}

.agent-header-layout {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  justify-content: space-between;
}

.agent-profile {
  align-items: center;
  display: flex;
  flex: 1 1 260px;
  gap: 16px;
  min-width: 0;
}

.agent-avatar {
  align-items: center;
  background: linear-gradient(135deg, #e8f7da 0%, #d6efc2 100%);
  border: 2px solid #ffffff;
  border-radius: 50%;
  box-shadow: 0 0 0 2px #37cf37, 0 4px 10px rgba(55, 207, 55, 0.18);
  color: #2f6b16;
  display: flex;
  flex: 0 0 52px;
  font-size: 17px;
  font-weight: 700;
  height: 52px;
  justify-content: center;
  width: 52px;
}

.agent-copy {
  min-width: 0;
}

.agent-copy h1 {
  color: #161616;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.25;
  margin: 0 0 4px;
}

.agent-copy p {
  align-items: center;
  color: #555a54;
  display: flex;
  flex-wrap: wrap;
  font-size: 13px;
  gap: 5px;
  line-height: 1.35;
  margin: 0 0 8px;
}

.copy-dot {
  background: #b8bbb4;
  border-radius: 50%;
  display: inline-block;
  height: 4px;
  width: 4px;
}

.last-seen-today {
  color: #2e7b21;
  font-weight: 600;
}

.last-seen-muted {
  color: #6f746e;
}

.badge-row,
.legend-row {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.status-badge {
  border-radius: 5px;
  display: inline-flex;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  padding: 5px 9px;
}

.status-badge-success {
  background: #e5f1d6;
  color: #2f6b16;
}

.status-badge-info {
  background: #e6f0ff;
  color: #335f9f;
}

.status-badge-offline,
.status-badge-neutral {
  background: #eaf6e3;
  border: 1px solid #cfe6bd;
  color: #2f6b16;
}

.header-controls {
  align-items: center;
  background: #f6faf3;
  border: 1px solid #e3eedb;
  border-radius: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
  min-width: 0;
  padding: 8px 10px;
}

.header-controls .control-select {
  flex: 1 1 145px;
  max-width: 220px;
  min-width: 130px;
}

.header-controls .agent-select {
  flex: 1 1 175px;
  max-width: 240px;
}

.header-controls .export-report-btn {
  flex: 0 0 auto;
}

.control-select >>> .v-input__slot {
  background: #ffffff !important;
  border-radius: 8px !important;
  box-shadow: 0 1px 2px rgba(15, 23, 14, 0.04);
  min-height: 42px !important;
}

.control-select >>> fieldset {
  border-color: #d6e3cf !important;
}

.export-report-btn {
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%) !important;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(55, 207, 55, 0.22) !important;
  height: 42px !important;
  letter-spacing: 0.2px;
  text-transform: none;
  white-space: nowrap;
}

.classic-view-toggle {
  align-items: center;
  background: #f6faf3;
  border: 1px solid #d6e9c7;
  border-radius: 999px;
  color: #333833;
  cursor: pointer;
  display: inline-flex;
  font-size: 13px;
  font-weight: 600;
  gap: 10px;
  height: 42px;
  justify-content: center;
  padding: 0 14px;
  transition: background 0.18s ease, border-color 0.18s ease,
    box-shadow 0.18s ease;
  white-space: nowrap;
}

.classic-view-toggle:hover {
  background: #ecf6e1;
  border-color: #b9dba0;
  box-shadow: 0 2px 6px rgba(55, 207, 55, 0.12);
}

.classic-view-toggle:focus-visible {
  border-radius: 999px;
  box-shadow: 0 0 0 3px rgba(25, 155, 56, 0.2);
  outline: none;
}

.classic-view-toggle:hover .classic-view-toggle-label {
  color: #199b38;
}

.classic-view-toggle-label {
  color: #6f746e;
  transition: color 0.18s ease;
}

.classic-view-classic-label {
  color: #199b38;
}

.classic-view-switch {
  background: #cfd4cd;
  border-radius: 999px;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.04);
  display: inline-flex;
  height: 20px;
  padding: 2px;
  transition: background 0.18s ease;
  width: 40px;
}

.classic-view-switch-thumb {
  background: #ffffff;
  border-radius: 50%;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.22);
  height: 16px;
  transition: transform 0.18s ease;
  width: 16px;
}

.classic-view-toggle-active .classic-view-classic-label {
  color: #6f746e;
}

.classic-view-toggle-active .classic-view-modern-label {
  color: #199b38;
}

.classic-view-toggle-active .classic-view-switch {
  background: #199b38;
}

.classic-view-toggle-active .classic-view-switch-thumb {
  transform: translateX(18px);
}

.performance-classic-view-toggle {
  flex: 0 0 auto;
}

.export-report-btn {
  color: #ffffff !important;
  padding: 0 16px !important;
}

.export-report-btn:hover {
  box-shadow: 0 6px 14px rgba(55, 207, 55, 0.3) !important;
}

.export-report-btn.v-btn--disabled {
  background: #cfd4cd !important;
  color: #ffffff !important;
}

.kpi-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  margin-bottom: 16px;
}

.kpi-card {
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 88px;
  padding: 14px 16px;
}

.kpi-label,
.kpi-sub {
  font-size: 11px;
  line-height: 1.35;
  margin: 0;
}

.kpi-label {
  color: rgba(0, 0, 0, 0.55);
  letter-spacing: 0.04em;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.kpi-sub {
  color: rgba(0, 0, 0, 0.55);
  font-weight: 500;
}

.kpi-value {
  color: #101010;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.1;
  margin: 0 0 4px;
}

.kpi-sub.down {
  color: #c0392b;
}

.kpi-sub.up {
  color: #1b8a4c;
}

.panel-grid {
  display: grid;
  gap: 12px;
}

.panel-grid-top {
  grid-template-columns: minmax(0, 3fr) minmax(310px, 2fr);
  margin-bottom: 14px;
}

.panel-grid-bottom {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.daily-card,
.disposition-card,
.attendance-card,
.quality-card {
  overflow: hidden;
  padding: 20px;
  position: relative;
}

.daily-card::before,
.disposition-card::before,
.attendance-card::before,
.quality-card::before {
  background: linear-gradient(90deg, #37cf37 0%, #2ba82b 100%);
  content: "";
  height: 4px;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
}

.section-heading {
  align-items: flex-start;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 16px;
}

.section-heading h2 {
  color: #151515;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.3;
  margin: 0 0 4px;
}

.section-heading p {
  color: #555a54;
  font-size: 12px;
  line-height: 1.35;
  margin: 0;
}

.legend-row {
  color: #555a54;
  font-size: 11px;
  line-height: 1.2;
}

.legend-row span {
  align-items: center;
  display: inline-flex;
  gap: 5px;
}

.legend-dot,
.calendar-key,
.quality-dot {
  display: inline-block;
  flex: 0 0 auto;
}

.legend-dot {
  border-radius: 2px;
  height: 9px;
  width: 9px;
}

.calls-dot {
  background: #4cd964;
}

.negative-dot {
  background: #7f77dd;
}

.active-dot {
  background: #f5d142;
}

.followup-dot {
  background: #ef9f27;
}

.positive-dot {
  background: #1d9e75;
}

.other-dot {
  background: #b4b2a9;
}

.daily-chart {
  align-items: end;
  border-bottom: 1px solid #deded9;
  display: grid;
  gap: 3px;
  min-height: 178px;
  padding: 8px 0 20px;
  position: relative;
}

.chart-guide {
  background: #e2e2dc;
  height: 1px;
  left: 0;
  position: absolute;
  right: 0;
  z-index: 0;
}

.chart-guide-top {
  top: 8px;
}

.chart-guide-mid {
  top: 34%;
}

.chart-guide-low {
  top: 66%;
}

.day-column {
  align-items: center;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-width: 0;
  position: relative;
  z-index: 1;
}

.bar-pair {
  align-items: flex-end;
  display: flex;
  gap: 2px;
  height: 132px;
  justify-content: center;
  width: 100%;
}

.bar-slot {
  align-items: flex-end;
  display: flex;
  height: 100%;
  justify-content: center;
  outline: none;
  position: relative;
  width: 38%;
}

.activity-bar {
  border-radius: 3px 3px 0 0;
  min-height: 4px;
  width: 100%;
}

.activity-tooltip {
  background: rgba(38, 38, 38, 0.94);
  border-radius: 5px;
  bottom: calc(100% + 8px);
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.24);
  color: #ffffff;
  display: flex;
  flex-direction: column;
  font-size: 13px;
  gap: 4px;
  left: 50%;
  line-height: 1.25;
  opacity: 0;
  padding: 8px 9px;
  pointer-events: none;
  position: absolute;
  transform: translate(-50%, 4px);
  transition: opacity 0.12s ease, transform 0.12s ease;
  white-space: nowrap;
  z-index: 20;
}

.activity-tooltip::after {
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid rgba(38, 38, 38, 0.94);
  bottom: -5px;
  content: "";
  left: 50%;
  position: absolute;
  transform: translateX(-50%);
}

.activity-tooltip strong {
  color: #ffffff;
  font-size: 13px;
  font-weight: 700;
}

.activity-tooltip span {
  align-items: center;
  color: #ffffff;
  display: inline-flex;
  font-size: 12px;
  line-height: 1.3;
  margin-top: 0;
}

.bar-slot:hover .activity-tooltip,
.bar-slot:focus .activity-tooltip {
  opacity: 1;
  transform: translate(-50%, 0);
}

.bar-slot:focus-visible .activity-bar {
  box-shadow: 0 0 0 2px rgba(38, 38, 38, 0.18);
}

.tooltip-dot {
  border-radius: 2px;
  display: inline-block;
  height: 10px;
  margin-right: 5px;
  width: 10px;
}

.tooltip-calls-dot {
  background: #4cd964;
}

.tooltip-active-dot {
  background: #f5d142;
}

.calls-bar {
  background: #4cd964;
}

.active-bar {
  background: #f5d142;
}

.stub-bar {
  background: #b4b2a9;
}

.day-column > span {
  color: #777b74;
  font-size: 9px;
  line-height: 1;
  margin-top: 7px;
}

.chart-note {
  border-top: 1px solid #ecece7;
  color: #73776f;
  font-size: 11px;
  margin: 12px 0 0;
  padding-top: 10px;
}

.disposition-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
}

.disposition-row {
  align-items: center;
  display: grid;
  gap: 8px;
  grid-template-columns: 112px minmax(0, 1fr) 34px;
}

.disposition-label {
  color: #343733;
  font-size: 11px;
  line-height: 1.2;
  overflow: hidden;
  text-align: right;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.disposition-track {
  background: #ddddda;
  border-radius: 999px;
  height: 7px;
  overflow: hidden;
}

.disposition-fill {
  border-radius: inherit;
  height: 100%;
}

.disposition-count {
  color: #161616;
  font-size: 11px;
  font-weight: 700;
}

.disposition-dialog-card {
  border-radius: 10px !important;
  padding: 20px;
}

.disposition-dialog-header {
  align-items: flex-start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 14px;
}

.disposition-dialog-header h2 {
  color: #151515;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.25;
  margin: 0 0 4px;
}

.disposition-dialog-header p {
  color: #555a54;
  font-size: 12px;
  line-height: 1.35;
  margin: 0;
}

.disposition-dialog-list {
  border: 1px solid #e4e4df;
  border-radius: 8px;
  max-height: 430px;
  overflow-y: auto;
}

.disposition-dialog-row {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  min-height: 40px;
  padding: 10px 12px;
}

.disposition-dialog-row + .disposition-dialog-row {
  border-top: 1px solid #eeeeea;
}

.disposition-dialog-name {
  align-items: center;
  color: #343733;
  display: flex;
  font-size: 13px;
  gap: 8px;
  min-width: 0;
}

.disposition-dialog-name span:last-child {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.disposition-dialog-dot {
  border-radius: 3px;
  flex: 0 0 auto;
  height: 10px;
  width: 10px;
}

.disposition-dialog-row strong {
  color: #151515;
  font-size: 13px;
  font-weight: 700;
}

.legend-row-bottom {
  border-top: 1px solid #e4e4df;
  margin-top: 16px;
  padding-top: 14px;
}

.empty-state {
  align-items: center;
  background: #f4f4f0;
  border-radius: 8px;
  color: #777b74;
  display: flex;
  font-size: 12px;
  justify-content: center;
  min-height: 172px;
}

.weekday-grid,
.calendar-grid {
  display: grid;
  gap: 6px;
  grid-template-columns: repeat(7, minmax(0, 1fr));
}

.weekday-grid {
  margin-bottom: 6px;
}

.weekday-grid span {
  color: #6c716b;
  font-size: 11px;
  text-align: center;
}

.calendar-cell {
  align-items: center;
  border: 1px solid transparent;
  border-radius: 5px;
  display: flex;
  height: 30px;
  justify-content: center;
}

.calendar-cell span {
  font-size: 11px;
  font-weight: 600;
}

.blank-cell {
  background: #f3f3ef;
}

.present-day {
  background: #e5f1d6;
  color: #2f6b16;
}

.absent-day {
  background: #f9e2e2;
  color: #a32d2d;
}

.future-day {
  background: #f3f3ef;
  color: #777b74;
}

.today-cell {
  border-color: #7f77dd;
  box-shadow: inset 0 0 0 1px rgba(127, 119, 221, 0.28);
  color: #534ab7;
}

.calendar-legend {
  gap: 12px;
}

.calendar-key {
  border-radius: 2px;
  height: 10px;
  width: 10px;
}

.present-key {
  background: #e5f1d6;
}

.absent-key {
  background: #f9e2e2;
}

.today-key {
  background: rgba(127, 119, 221, 0.12);
  border: 1px solid rgba(127, 119, 221, 0.45);
}

.quality-bar {
  background: #f9e2e2;
  border-radius: 7px;
  display: flex;
  height: 25px;
  margin-bottom: 18px;
  overflow: hidden;
}

.quality-segment {
  align-items: center;
  display: flex;
  justify-content: center;
  min-width: 0;
}

.quality-segment span {
  font-size: 10px;
  font-weight: 700;
  white-space: nowrap;
}

.segment-active {
  background: #bfe8a5;
  color: #2f6b16;
}

.segment-idle {
  background: #f9e2e2;
  color: #a32d2d;
}

.quality-list {
  display: flex;
  flex-direction: column;
  gap: 13px;
}

.quality-row {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.quality-label-wrap,
.quality-value-wrap {
  align-items: center;
  display: flex;
}

.quality-label-wrap {
  color: #222421;
  font-size: 13px;
  gap: 8px;
}

.quality-dot {
  border-radius: 2px;
  height: 10px;
  width: 10px;
}

.quality-active {
  background: #bfe8a5;
  border: 1px solid #8dcc6d;
}

.quality-idle {
  background: #f9e2e2;
  border: 1px solid #ee9f9f;
}

.quality-value-wrap {
  gap: 8px;
}

.quality-value-wrap strong {
  color: #111111;
  font-size: 13px;
}

.quality-value-wrap span {
  color: #555a54;
  font-size: 11px;
}

.idle-warning {
  background: #f3e9d7;
  border-radius: 8px;
  margin-top: 18px;
  padding: 12px;
}

.idle-warning p,
.idle-warning span {
  color: #765718;
  display: block;
  line-height: 1.35;
  margin: 0;
}

.idle-warning p {
  font-size: 12px;
  font-weight: 700;
}

.idle-warning span {
  font-size: 11px;
  margin-top: 2px;
}

.theme--dark .performance-v2-container {
  background: #121212 !important;
}

.theme--dark .dashboard-card {
  background: #1e1e1e;
  border-color: #333333;
}

.theme--dark .agent-copy h1,
.theme--dark .section-heading h2,
.theme--dark .kpi-value,
.theme--dark .quality-value-wrap strong,
.theme--dark .quality-label-wrap,
.theme--dark .disposition-count {
  color: #f5f5f5;
}

.theme--dark .agent-copy p,
.theme--dark .section-heading p,
.theme--dark .legend-row,
.theme--dark .kpi-label,
.theme--dark .kpi-sub,
.theme--dark .disposition-label,
.theme--dark .chart-note {
  color: #bdbdbd;
}

.theme--dark .kpi-card,
.theme--dark .empty-state,
.theme--dark .blank-cell {
  background: #292929;
}

@media (max-width: 960px) {
  .performance-v2-shell {
    padding: 12px;
  }

  .performance-hero-chips {
    margin-top: 16px;
    padding-right: 0 !important;
    text-align: left !important;
  }

  .page-title {
    font-size: 22px;
  }

  .header-icon-wrapper {
    border-radius: 12px;
    height: 48px;
    width: 48px;
  }

  .header-icon-wrapper .v-icon {
    font-size: 24px !important;
  }

  .agent-header-layout,
  .agent-profile {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
  }

  .header-controls {
    justify-content: flex-start;
    width: 100%;
  }

  .header-controls .control-select,
  .header-controls .agent-select {
    flex: 1 1 100%;
    max-width: 100%;
  }

  .header-controls .export-report-btn {
    width: 100%;
  }

  .performance-classic-view-toggle {
    align-self: center;
  }

  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .panel-grid-top,
  .panel-grid-bottom {
    grid-template-columns: 1fr;
  }

  .daily-card,
  .disposition-card,
  .attendance-card,
  .quality-card {
    padding: 16px;
  }

  .daily-heading {
    flex-direction: column;
  }

  .calendar-cell {
    height: 24px;
  }
}

@media (max-width: 520px) {
  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .kpi-card {
    padding: 12px;
  }

  .kpi-value {
    font-size: 22px;
  }

  .disposition-row {
    grid-template-columns: 92px minmax(0, 1fr) 30px;
  }
}
</style>